import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, Output, Input, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/forms';
import { Validators, ReactiveFormsModule } from '@angular/forms';
import * as i6 from '@roosevelt/common-ui-lib/button';
import { ButtonModule } from '@roosevelt/common-ui-lib/button';
import * as i5 from '@roosevelt/common-ui-lib/select';
import { SelectModule } from '@roosevelt/common-ui-lib/select';
import * as i4 from '@roosevelt/common-ui-lib/loader';
import { LoaderModule } from '@roosevelt/common-ui-lib/loader';
import * as i1$1 from '@roosevelt/common-ui-lib/core';
import { first, tap, mergeMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import * as i1 from '@angular/common/http';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RadioModule } from '@roosevelt/common-ui-lib/radio';

class DentistDirEnvConfig {
    constructor() {
        this.statesUrl = '';
        this.dentistNetworksUrl = '';
        this.stateInfoUrl = '';
        this.dentistDirectoryDocumentUrl = '';
    }
}
const envProvider = {
    provide: DentistDirEnvConfig,
    useValue: {
        statesUrl: '',
        dentistNetworksUrl: '',
        stateInfoUrl: '',
        dentistDirectoryDocumentUrl: '',
    }
};

const isFetchingDentistDir = (state) => state.dentistDirState.isFetchingDentistDir;
const dentistDirError = (state) => state.dentistDirState.dentistDirError;

class DentistDirApiService {
    constructor(http, config, internalAuth) {
        this.http = http;
        this.config = config;
        this.internalAuth = internalAuth;
    }
    getStatesList() {
        return this.http.get(this.config.statesUrl);
    }
    getNetworks(adminPayer) {
        return this.http.get(this.config.dentistNetworksUrl + '?adminPayer=' + adminPayer);
    }
    getStateInfo(state) {
        const url = this.config.stateInfoUrl + state;
        if (this.internalAuth.authedUrls.indexOf(url) === -1) {
            this.internalAuth.authedUrls = [...this.internalAuth.authedUrls, url];
        }
        return this.http.get(url);
    }
    createDDirectory(req) {
        return this.http.post(this.config.dentistDirectoryDocumentUrl, req, {
            headers: { Accept: 'application/pdf' },
            observe: 'response',
            responseType: 'arraybuffer'
        });
    }
    createDDirectoryExcel(req) {
        return this.http.post(this.config.dentistDirectoryDocumentUrl, req, {
            headers: { accept: 'application/vnd.ms-excel' },
            observe: 'response',
            responseType: 'arraybuffer'
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DentistDirApiService, deps: [{ token: i1.HttpClient }, { token: DentistDirEnvConfig }, { token: i1$1.BasicAuthConfig }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DentistDirApiService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DentistDirApiService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: DentistDirEnvConfig }, { type: i1$1.BasicAuthConfig }] });

class DentistDirAsync {
    constructor(lux, dDService) {
        this.lux = lux;
        this.dDService = dDService;
    }
    getStates() {
        return this.dDService.getStatesList();
    }
    getNetworks(adminPayer) {
        return of(adminPayer).pipe(first(), tap(() => this.lux.set(x => x.dentistDirState, { isFetchingDentistDir: true, dentistDirError: '' })), mergeMap(x => this.dDService.getNetworks(x)), tap(res => {
            this.lux.set(x => x.dentistDirState, { isFetchingDentistDir: false });
        }), catchError((err) => {
            let errorMessage = '';
            if (err && err.error && err.error.apiErrorList && err.error.apiErrorList.length > 0) {
                errorMessage = err.error.apiErrorList.map((item) => item.errorMessage).join('\n');
            }
            this.lux.set(x => x.dentistDirState, { dentistDirError: errorMessage, isFetchingDentistDir: false });
            return of(errorMessage);
        }));
    }
    getStateInfo(state) {
        return of(state).pipe(first(), tap(() => this.lux.set(x => x.dentistDirState, { isFetchingDentistDir: true, dentistDirError: '' })), mergeMap(x => this.dDService.getStateInfo(x)), tap(res => {
            this.lux.set(x => x.dentistDirState, { isFetchingDentistDir: false });
        }), catchError((err) => {
            let errorMessage = '';
            if (err && err.error && err.error.apiErrorList && err.error.apiErrorList.length > 0) {
                errorMessage = err.error.apiErrorList.map((item) => item.errorMessage).join('\n');
            }
            this.lux.set(x => x.dentistDirState, { dentistDirError: errorMessage, isFetchingDentistDir: false });
            return of(errorMessage);
        }));
    }
    createDDirectory(req) {
        return of(req).pipe(first(), tap(() => this.lux.set(x => x.dentistDirState, { isFetchingDentistDir: true, dentistDirError: '' })), mergeMap(x => this.dDService.createDDirectory(x)), tap((res) => {
            const contentDisposition = res.headers.get('content-disposition') || '';
            const fileName = contentDisposition.split(';')[1].trim().split('=')[1].replace(/"/g, '');
            this.lux.set(x => x.dentistDirState, { isFetchingDentistDir: false });
            const file = new Blob([res.body], { type: 'application/pdf' });
            // WHEN POPUPs ARE BLOCKED
            const url = URL.createObjectURL(file);
            const anchor = document.createElement("a");
            anchor.download = fileName;
            anchor.href = url;
            anchor.click();
        }), catchError((e) => {
            let err = bufferToString(e.error);
            if (err && err.apiErrorList && err.apiErrorList.length > 0) {
                err = err.apiErrorList.map((item) => item.errorMessage).join('\n');
            }
            this.lux.set(x => x.dentistDirState, { dentistDirError: err || 'No records found.', isFetchingDentistDir: false });
            return of(err);
        }));
    }
    createDDirectoryExcel(req) {
        return of(req).pipe(first(), tap(() => this.lux.set(x => x.dentistDirState, { isFetchingDentistDir: true, dentistDirError: '' })), mergeMap(x => this.dDService.createDDirectoryExcel(x)), tap((res) => {
            const contentDisposition = res.headers.get('content-disposition') || '';
            const fileName = contentDisposition.split(';')[1].trim().split('=')[1].replace(/"/g, '');
            this.lux.set(x => x.dentistDirState, { isFetchingDentistDir: false });
            const file = new Blob([res.body], { type: 'application/vnd.ms-excel' });
            // WHEN POPUPs ARE BLOCKED
            setTimeout(() => {
                const url = URL.createObjectURL(file);
                const anchor = document.createElement("a");
                anchor.download = fileName;
                anchor.href = url;
                anchor.click();
            }, 0);
        }), catchError((e) => {
            let err = bufferToString(e.error);
            if (err && err.apiErrorList && err.apiErrorList.length > 0) {
                err = err.apiErrorList.map((item) => item.errorMessage).join('\n');
            }
            this.lux.set(x => x.dentistDirState, { dentistDirError: err || 'No records found.', isFetchingDentistDir: false });
            return of(err);
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DentistDirAsync, deps: [{ token: i1$1.Lux }, { token: DentistDirApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DentistDirAsync, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DentistDirAsync, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.Lux }, { type: DentistDirApiService }] });
function bufferToString(buf) {
    return JSON.parse(String.fromCharCode.apply(null, [...new Uint8Array(buf)]));
}

class DentistDirComponent {
    get selectedAdminPlan() {
        return this.selectedAdminPlan_;
    }
    set selectedAdminPlan(val) {
        if (val) {
            this.selectedAdminPlan_ = val;
            this.getNetworkOptions(val);
        }
    }
    constructor(lux, fb, dDAsync) {
        this.lux = lux;
        this.fb = fb;
        this.dDAsync = dDAsync;
        this.genDentistDir = new EventEmitter();
        this.loaderImage = '';
        this.selectedAdminPlan_ = '';
        this.luxId = 'dentist-dir-lib-dentist-dir-comp' + this.constructor.name;
        this.dentistDirForm = undefined;
        this.searchBy = '';
        this.directory = [
            {
                label: '',
                value: ''
            },
            {
                label: 'County',
                value: 'counties'
            },
            {
                label: 'Zip Code',
                value: 'zipCodes'
            },
            {
                label: 'Three Digit Zip Code',
                value: 'threeDigitZipCodes'
            }
        ];
        this.allStates = [{
                label: '',
                value: ''
            }];
        this.networkDropDown = [{
                label: '',
                value: ''
            }];
        this.countyDropDown = [];
        this.zipDropDown = [];
        this.zipExtDropDown = [];
        this.dentistDirError$ = this.lux.get(dentistDirError, this.luxId);
        this.error = '';
        this.disableDownloadExcel = false;
        this.isFetchingDentistDir$ = this.lux.get(isFetchingDentistDir, this.luxId);
        this.isFetching = false;
    }
    ngOnInit() {
        this.dentistDirError$.subscribe(x => this.error = x);
        this.isFetchingDentistDir$.subscribe(x => this.isFetching = x);
        this.dentistDirForm = this.fb.group({
            network: ['', Validators.required],
            state: ['', Validators.required],
            counties: [{ value: '', disabled: true }, Validators.required],
            zipCodes: [{ value: '', disabled: true }, Validators.required],
            threeDigitZipCodes: [{ value: '', disabled: true }, Validators.required],
            directoryBy: '',
            language: 'English'
        });
        this.dDAsync.getStates().subscribe(states => {
            this.allStates = states.map(x => {
                return {
                    label: x.value,
                    value: x.code
                };
            });
        });
        this.dentistDirForm.controls.language.valueChanges.subscribe(val => {
            this.disableDownloadExcel = val === 'Spanish';
        });
    }
    getNetworkOptions(adminPayer) {
        this.dDAsync.getNetworks(adminPayer).subscribe(networks => {
            this.networkDropDown = networks.map(x => {
                return {
                    label: x.name,
                    value: x.type
                };
            });
        });
    }
    getStateInfo() {
        this.resetDentistDir();
        this.dDAsync.getStateInfo(this.dentistDirForm.controls.state.value).subscribe(info => {
            this.countyDropDown = info.counties;
            this.zipDropDown = info.zipCodes;
            this.zipExtDropDown = info.threeDigitZipCodes;
        });
    }
    directoryBy() {
        ['counties', 'zipCodes', 'threeDigitZipCodes'].forEach(key => {
            this.dentistDirForm.controls[key].disable();
            this.dentistDirForm.controls[key].setValue('');
        });
        this.searchBy = this.dentistDirForm.controls.directoryBy.value;
        this.dentistDirForm.controls[this.searchBy].enable();
    }
    resetDentistDir() {
        this.searchBy = this.error = '';
        ['counties', 'zipCodes', 'threeDigitZipCodes'].forEach(key => {
            this.dentistDirForm.controls[key].disable();
            this.dentistDirForm.controls[key].setValue('');
        });
        this.dentistDirForm.controls['directoryBy'].setValue('');
        this.dentistDirForm.controls['language'].setValue('English');
    }
    createDentistDir(type) {
        const req = this.dentistDirForm.value;
        req.threeDigitZipCodes && req.threeDigitZipCodes.length ? { ...req.zipCodes = req.threeDigitZipCodes } : null;
        delete req.threeDigitZipCodes;
        delete req.directoryBy;
        type === 'pdf' ? this.dDAsync.createDDirectory(req).subscribe() : this.dDAsync.createDDirectoryExcel(req).subscribe();
        this.genDentistDir.emit(type);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DentistDirComponent, deps: [{ token: i1$1.Lux }, { token: i2.UntypedFormBuilder }, { token: DentistDirAsync }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: DentistDirComponent, isStandalone: true, selector: "dentist-directory", inputs: { loaderImage: "loaderImage", selectedAdminPlan: "selectedAdminPlan" }, outputs: { genDentistDir: "genDentistDir" }, ngImport: i0, template: "<div class=\"dentist-dir-component\">\n  @if (error) {\n    <div class=\"dentist-dir-error\">{{error}}</div>\n  }\n  @if (isFetching) {\n    <rs-loader position=\"absolute\" imageSrc={{loaderImage}}></rs-loader>\n  }\n  <form [formGroup]=\"dentistDirForm\" #ngDDForm=\"ngForm\">\n    <div>\n      <rs-select class=\"dentist-dir-column col-sm-3\" formControlName=\"network\" label=\"Select a Network\" validation=\"eager\" [items]=\"networkDropDown\"></rs-select>\n      <rs-select class=\"dentist-dir-column col-sm-2\" formControlName=\"state\" label=\"State\" validation=\"eager\" [items]=\"allStates\" (change)=\"getStateInfo()\"></rs-select>\n      <rs-select class=\"dentist-dir-column col-sm-2\" formControlName=\"directoryBy\" label=\"Search directory by\" [items]=\"directory\" validation=\"eager\" (change)=\"directoryBy()\"></rs-select>\n      @if (searchBy === 'counties') {\n        <rs-select class=\"dentist-dir-column col-sm-3\" formControlName=\"counties\" label=\"County\" [multiple]=true [searchable]=true validation=\"eager\" [items]=\"countyDropDown\"></rs-select>\n      }\n      @if (searchBy === 'zipCodes') {\n        <rs-select class=\"dentist-dir-column col-sm-3\" formControlName=\"zipCodes\" label=\"Zip Code\" [multiple]=true [searchable]=true validation=\"eager\" [items]=\"zipDropDown\"></rs-select>\n      }\n      @if (searchBy === 'threeDigitZipCodes') {\n        <rs-select class=\"dentist-dir-column col-sm-3\" formControlName=\"threeDigitZipCodes\" label=\"Three Digit Zip Code\" [multiple]=true [searchable]=true validation=\"eager\" [items]=\"zipExtDropDown\"></rs-select>\n      }\n      <rs-select class=\"dentist-dir-column col-sm-2\" formControlName=\"language\" label=\"Language\" validation=\"eager\" [items]=\"['English', 'Spanish']\"></rs-select>\n    </div>\n    <div class=\"dentist-buttons\">\n      <div class=\"dentist-button\">\n        <button rs-button type=\"button\" (click)=\"ngDDForm.resetForm(); resetDentistDir()\">Reset</button>\n        <button style=\"margin-right: 10px\" [disabled]=\"!dentistDirForm.valid\" rs-raised-button color=\"primary\" (click)=\"createDentistDir('pdf')\">Create Dentist Directory</button>\n        <button [disabled]=\"!dentistDirForm.valid || disableDownloadExcel\" rs-raised-button color=\"primary\" (click)=\"createDentistDir('excel')\">Create Dentist Directory - Excel</button>\n      </div>\n    </div>\n  </form>\n  <div class=\"dentist-dir-note\"><strong>Please note: </strong>If you have the Delta Dental PPO Plus Premier product, you may choose from either the Delta Dental Premier or the Delta Dental PPO network for a participating provider.</div>\n</div>\n", styles: [".dentist-dir-component{position:relative;width:100%;height:195px}.dentist-dir-component .dentist-dir-error{color:red;padding:5px;font-weight:700}.dentist-dir-component .dentist-dir-column{float:left;padding:0 5px}.dentist-dir-component .dentist-dir-note{float:left;padding:15px 10px}.dentist-dir-component .dentist-buttons .dentist-button{padding-top:15px;float:right}\n"], dependencies: [{ kind: "ngmodule", type: LoaderModule }, { kind: "component", type: i4.LoaderComponent, selector: "rs-loader", inputs: ["position", "imageSrc", "color"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "ngmodule", type: SelectModule }, { kind: "component", type: i5.SelectComponent, selector: "rs-select", inputs: ["label", "placeholder", "validation", "errors", "appendTo", "hideErrorMessage", "id", "multiple", "searchable", "clearable", "items", "selectValue", "tabindex", "disabled", "color"], outputs: ["change", "close"] }, { kind: "ngmodule", type: ButtonModule }, { kind: "component", type: i6.ButtonComponent, selector: "button[rs-button], button[rs-raised-button], button[rs-outlined-button]", inputs: ["color"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DentistDirComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dentist-directory', standalone: true, imports: [LoaderModule, ReactiveFormsModule, SelectModule, ButtonModule], template: "<div class=\"dentist-dir-component\">\n  @if (error) {\n    <div class=\"dentist-dir-error\">{{error}}</div>\n  }\n  @if (isFetching) {\n    <rs-loader position=\"absolute\" imageSrc={{loaderImage}}></rs-loader>\n  }\n  <form [formGroup]=\"dentistDirForm\" #ngDDForm=\"ngForm\">\n    <div>\n      <rs-select class=\"dentist-dir-column col-sm-3\" formControlName=\"network\" label=\"Select a Network\" validation=\"eager\" [items]=\"networkDropDown\"></rs-select>\n      <rs-select class=\"dentist-dir-column col-sm-2\" formControlName=\"state\" label=\"State\" validation=\"eager\" [items]=\"allStates\" (change)=\"getStateInfo()\"></rs-select>\n      <rs-select class=\"dentist-dir-column col-sm-2\" formControlName=\"directoryBy\" label=\"Search directory by\" [items]=\"directory\" validation=\"eager\" (change)=\"directoryBy()\"></rs-select>\n      @if (searchBy === 'counties') {\n        <rs-select class=\"dentist-dir-column col-sm-3\" formControlName=\"counties\" label=\"County\" [multiple]=true [searchable]=true validation=\"eager\" [items]=\"countyDropDown\"></rs-select>\n      }\n      @if (searchBy === 'zipCodes') {\n        <rs-select class=\"dentist-dir-column col-sm-3\" formControlName=\"zipCodes\" label=\"Zip Code\" [multiple]=true [searchable]=true validation=\"eager\" [items]=\"zipDropDown\"></rs-select>\n      }\n      @if (searchBy === 'threeDigitZipCodes') {\n        <rs-select class=\"dentist-dir-column col-sm-3\" formControlName=\"threeDigitZipCodes\" label=\"Three Digit Zip Code\" [multiple]=true [searchable]=true validation=\"eager\" [items]=\"zipExtDropDown\"></rs-select>\n      }\n      <rs-select class=\"dentist-dir-column col-sm-2\" formControlName=\"language\" label=\"Language\" validation=\"eager\" [items]=\"['English', 'Spanish']\"></rs-select>\n    </div>\n    <div class=\"dentist-buttons\">\n      <div class=\"dentist-button\">\n        <button rs-button type=\"button\" (click)=\"ngDDForm.resetForm(); resetDentistDir()\">Reset</button>\n        <button style=\"margin-right: 10px\" [disabled]=\"!dentistDirForm.valid\" rs-raised-button color=\"primary\" (click)=\"createDentistDir('pdf')\">Create Dentist Directory</button>\n        <button [disabled]=\"!dentistDirForm.valid || disableDownloadExcel\" rs-raised-button color=\"primary\" (click)=\"createDentistDir('excel')\">Create Dentist Directory - Excel</button>\n      </div>\n    </div>\n  </form>\n  <div class=\"dentist-dir-note\"><strong>Please note: </strong>If you have the Delta Dental PPO Plus Premier product, you may choose from either the Delta Dental Premier or the Delta Dental PPO network for a participating provider.</div>\n</div>\n", styles: [".dentist-dir-component{position:relative;width:100%;height:195px}.dentist-dir-component .dentist-dir-error{color:red;padding:5px;font-weight:700}.dentist-dir-component .dentist-dir-column{float:left;padding:0 5px}.dentist-dir-component .dentist-dir-note{float:left;padding:15px 10px}.dentist-dir-component .dentist-buttons .dentist-button{padding-top:15px;float:right}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.Lux }, { type: i2.UntypedFormBuilder }, { type: DentistDirAsync }], propDecorators: { genDentistDir: [{
                type: Output
            }], loaderImage: [{
                type: Input
            }], selectedAdminPlan: [{
                type: Input
            }] } });

const initialDentistDirState = {
    isFetchingDentistDir: false,
    dentistDirError: ''
};

class UserAuthConfig {
    constructor() {
        this.userAuthorizations = null;
    }
}
const userAuthProvider = {
    provide: UserAuthConfig,
    useValue: {
        userAuthorizations: {
            userId: "",
            contextBasedAuthorizations: {
                ADMINPLAN: [],
            },
            uiAuthorizations: [],
            roles: [],
            resources: [],
        },
    },
};

class ServicesModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ServicesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: ServicesModule, imports: [CommonModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ServicesModule, providers: [envProvider, userAuthProvider, provideHttpClient(withInterceptorsFromDi())], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ServicesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule],
                    providers: [envProvider, userAuthProvider, provideHttpClient(withInterceptorsFromDi())]
                }]
        }] });

class DentistDirModule {
    constructor(lux) {
        const dentistDirState = JSON.parse(sessionStorage.getItem('dentist-dir-state'));
        lux.register('dentistDirState', dentistDirState ? dentistDirState : initialDentistDirState);
        lux.allChanges().subscribe(state => sessionStorage.setItem('dentist-dir-state', JSON.stringify(state.dentistDirState)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DentistDirModule, deps: [{ token: i1$1.Lux }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: DentistDirModule, imports: [CommonModule,
            SelectModule,
            ReactiveFormsModule,
            RadioModule,
            ServicesModule,
            ButtonModule,
            LoaderModule,
            DentistDirComponent], exports: [DentistDirComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DentistDirModule, imports: [CommonModule,
            SelectModule,
            ReactiveFormsModule,
            RadioModule,
            ServicesModule,
            ButtonModule,
            LoaderModule,
            DentistDirComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: DentistDirModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        DentistDirComponent
                    ],
                    imports: [
                        CommonModule,
                        SelectModule,
                        ReactiveFormsModule,
                        RadioModule,
                        ServicesModule,
                        ButtonModule,
                        LoaderModule,
                        DentistDirComponent
                    ]
                }]
        }], ctorParameters: () => [{ type: i1$1.Lux }] });

/*
 * Public API Surface of dentist-dir-lib
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DentistDirComponent, DentistDirEnvConfig, DentistDirModule, envProvider };
